// frontend/src/pages/BookingDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bookingsAPI } from '../services/api';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const bookingData = await bookingsAPI.getBookingDetails(bookingId);
      setBooking(bookingData);
    } catch (err) {
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <Loading message="Loading booking details..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <ErrorMessage message={error} />
          <div className="mt-4">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Booking Not Found</h1>
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const flightDetails = booking.flight_details || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-500 mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Booking Details</h1>
          </div>

          {/* Booking Summary */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {flightDetails.origin_city || 'N/A'} → {flightDetails.destination_city || 'N/A'}
                </h2>
                <p className="text-gray-600">
                  Booking Reference: <span className="font-medium text-blue-600">{booking.booking_reference}</span>
                </p>
              </div>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(booking.booking_status)}`}>
                {booking.booking_status.charAt(0).toUpperCase() + booking.booking_status.slice(1)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Total Amount</h3>
                <p className="text-2xl font-bold text-green-600">
                  ${booking.total_amount ? booking.total_amount.toFixed(2) : '0.00'}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Payment Status</h3>
                <p className={`font-medium ${booking.payment_status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {booking.payment_status.charAt(0).toUpperCase() + booking.payment_status.slice(1)}
                </p>
              </div>
            </div>
          </div>

          {/* Flight Information */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Flight Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Flight Details</h3>
                <p className="text-gray-600">
                  <span className="font-medium">{flightDetails.airline_name || 'N/A'}</span>
                </p>
                <p className="text-gray-600">Flight {flightDetails.flight_number || 'N/A'}</p>
                <p className="text-gray-600">{flightDetails.aircraft_type || 'N/A'}</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">Route</h3>
                <p className="text-gray-600">
                  <span className="font-medium">{flightDetails.origin_city || 'N/A'}</span> ({flightDetails.origin || 'N/A'})
                </p>
                <p className="text-gray-600">to</p>
                <p className="text-gray-600">
                  <span className="font-medium">{flightDetails.destination_city || 'N/A'}</span> ({flightDetails.destination || 'N/A'})
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">Schedule</h3>
                <p className="text-gray-600">
                  <span className="font-medium">Departure:</span><br />
                  {formatDate(flightDetails.departure_time)}<br />
                  {formatTime(flightDetails.departure_time)}
                </p>
                <p className="text-gray-600 mt-2">
                  <span className="font-medium">Arrival:</span><br />
                  {formatDate(flightDetails.arrival_time)}<br />
                  {formatTime(flightDetails.arrival_time)}
                </p>
                <p className="text-gray-600 mt-2">
                  <span className="font-medium">Duration:</span> {formatDuration(flightDetails.duration)}
                </p>
              </div>
            </div>
          </div>

          {/* Passenger Information */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Passenger Information</h2>
            
            <div className="space-y-4">
              {booking.passengers && booking.passengers.map((passenger, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Passenger {index + 1}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <p className="font-medium">{passenger.first_name} {passenger.last_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Date of Birth:</span>
                      <p>{passenger.date_of_birth ? new Date(passenger.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Gender:</span>
                      <p className="capitalize">{passenger.gender || 'N/A'}</p>
                    </div>
                  </div>
                  {passenger.passport_number && (
                    <div className="mt-2">
                      <span className="text-gray-600 text-sm">Passport:</span>
                      <p className="text-sm">{passenger.passport_number}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Email</h3>
                <p className="text-gray-600">{booking.contact_email}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Phone</h3>
                <p className="text-gray-600">{booking.contact_phone}</p>
              </div>
            </div>

            {booking.special_requests && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-900 mb-2">Special Requests</h3>
                <p className="text-gray-600">{booking.special_requests}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Actions</h2>
            
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => window.print()}
                className="btn btn-secondary"
              >
                Print Booking
              </button>
              
              <Link to="/dashboard" className="btn btn-primary">
                Back to Dashboard
              </Link>

              {booking.booking_status === 'confirmed' && (
                <button className="btn btn-danger">
                  Cancel Booking
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;